import React, { useState, useEffect, useRef } from 'react';
import SubscriptionPlanCard from './SubscriptionPlanCard';
import { SubscriptionsPlansProps } from '../../../core/types/_models';
import { getSubscriptionPlans } from '../../../core/requests/requests';
import { SelectSubscriptionProps } from './confirmationComponentsModels';

const SelectSubscription: React.FC<SelectSubscriptionProps> = ({
  isUpdating,
  closeSubscriptionModal,
}) => {
  const [subscriptionPlans, setSubscriptionPlans] = useState<
    SubscriptionsPlansProps[]
  >([]);
  const [itemsPerPage, setItemsPerPage] = useState(isUpdating ? 3 : 2);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 1150) setItemsPerPage(1);
      else if (width < 1380) setItemsPerPage(2);
      else setItemsPerPage(isUpdating ? 2 : 3);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data } = await getSubscriptionPlans();
        if (!data) return;

        let plans = data;

        if (isUpdating) {
          // Remove any plan with type "Free"
          plans = data.filter((p) => p.type !== 'Free');
        }

        setSubscriptionPlans(plans);
      } catch (error) {
        console.error('Error fetching subscription plans:', error);
      }
    };

    fetchPlans();
  }, [isUpdating]);

  const groupPlansByType = (plans: SubscriptionsPlansProps[]) =>
    plans.reduce(
      (acc, plan) => {
        if (!acc[plan.type]) acc[plan.type] = [];
        acc[plan.type].push(plan);
        return acc;
      },
      {} as Record<string, SubscriptionsPlansProps[]>
    );

  const customOrder = ['Pro', 'Basic', 'Free'];
  const groupedPlans = groupPlansByType(subscriptionPlans);
  const sortedGroupedPlans = Object.entries(groupedPlans).sort(
    ([a], [b]) => customOrder.indexOf(a) - customOrder.indexOf(b)
  );

  const totalItems = sortedGroupedPlans.length;

  const modIndex = (n: number) => {
    return totalItems > 0 ? ((n % totalItems) + totalItems) % totalItems : 0;
  };

  const getVisiblePlans = () => {
    if (totalItems === 0) return [];
    return Array.from({ length: itemsPerPage }, (_, i) => {
      const index = modIndex(currentIndex + i);
      return sortedGroupedPlans[index];
    });
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => modIndex(prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => modIndex(prev + 1));
  };

  const visiblePlans = getVisiblePlans();

  return (
    <div className="subscription-carousel-wrapper">
      {isUpdating && itemsPerPage < 2 && totalItems > 0 ? (
        <button className="carousel-arrow left " onClick={handlePrev}>
          &#9664;
        </button>
      ) : (
        !isUpdating &&
        itemsPerPage < 3 &&
        totalItems > 0 && (
          <button className="carousel-arrow left " onClick={handlePrev}>
            &#9664;
          </button>
        )
      )}
      <div className={`subscription-carousel items-${itemsPerPage}`}>
        {visiblePlans.length > 0 ? (
          visiblePlans.map((entry, i) => {
            if (!Array.isArray(entry)) return null;
            const [type, plans] = entry;
            return (
              <div key={type} className="carousel-slide">
                <SubscriptionPlanCard
                  subscriptionPlans={plans}
                  isUpdating={isUpdating}
                  closeSubscriptionModal={closeSubscriptionModal}
                />
              </div>
            );
          })
        ) : (
          <div className="subscription-carousel__empty">
            <span>No subscription plans available.</span>
          </div>
        )}
      </div>
      {isUpdating && itemsPerPage < 2 && totalItems > 0 ? (
        <button className="carousel-arrow right" onClick={handleNext}>
          &#9654;
        </button>
      ) : (
        !isUpdating &&
        itemsPerPage < 3 &&
        totalItems > 0 && (
          <button className="carousel-arrow right" onClick={handleNext}>
            &#9654;
          </button>
        )
      )}
    </div>
  );
};

export default SelectSubscription;
